import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { flushSync } from 'react-dom';
import QuizTitle from './QuizTitle';
import LazyImage from './LazyImage';
import { useTranslation } from 'react-i18next';
import { useVideoAnimation } from '../../hooks/useVideoAnimation';
import { withFirstFramePoster } from '../../utils/video';

const VideoCarousel = ({
  items,
  title,
  onSelect,
  isTransitioning = false,
  imageContainerClass = 'kinks-video-container',
  forceShowVideos = false,
}) => {
  const { t } = useTranslation();
  const {
    animationMode,
    isMobile,
    shouldPlayOnHoverOrSelected,
    shouldShowVideos,
    getVideoProps,
  } = useVideoAnimation();

  const totalItems = items.length;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [translateX, setTranslateX] = useState(0);
  const [animatingDirection, setAnimatingDirection] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [videoErrors, setVideoErrors] = useState({});
  const videoRefs = useRef({});

  const getRealIndex = (index) => {
    return ((index % totalItems) + totalItems) % totalItems;
  };

  // Update video sources imperatively to avoid reloading
  useEffect(() => {
    [-1, 0, 1].forEach((offset) => {
      const video = videoRefs.current[offset];
      const index = getRealIndex(currentIndex + offset);
      const item = items[index];
      const videoSource = item?.video || item?.videoSrc;

      if (video && videoSource) {
        // Only update src if it's different to avoid unnecessary reloads
        // Compare normalized URLs (video.src is always absolute URL)
        const targetSrc = withFirstFramePoster(videoSource);
        const currentSrc = video.src;
        const newSrc = targetSrc.startsWith('http') ? targetSrc : new URL(targetSrc, window.location.origin).href;

        if (currentSrc !== newSrc) {
          const wasPlaying = !video.paused;
          const currentTime = video.currentTime;
          video.src = targetSrc;

          // Restore playback state if needed
          if (wasPlaying && offset === 0) {
            video.currentTime = currentTime;
            video.play().catch(() => {});
          }
        }
      }
    });
  }, [currentIndex, items]);

  useEffect(() => {
    if (!forceShowVideos && !shouldShowVideos) return;

    [-1, 0, 1].forEach((offset) => {
      const video = videoRefs.current[offset];
      if (video) {
        if (offset === 0 && !shouldPlayOnHoverOrSelected) {
          video.currentTime = 0;
          video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  }, [
    currentIndex,
    shouldShowVideos,
    shouldPlayOnHoverOrSelected,
    forceShowVideos,
  ]);

  useEffect(() => {
    if (!shouldPlayOnHoverOrSelected || (!forceShowVideos && !shouldShowVideos))
      return;

    Object.keys(videoRefs.current).forEach((key) => {
      const video = videoRefs.current[key];
      if (video && !video.paused) {
        video.pause();
        video.currentTime = 0;
      }
    });

    const activeVideo = videoRefs.current[0]; // offset 0 = активный элемент
    if (activeVideo && activeVideo.paused) {
      activeVideo.currentTime = 0;
      activeVideo.play().catch(() => {});
    }
  }, [
    currentIndex,
    shouldPlayOnHoverOrSelected,
    shouldShowVideos,
    forceShowVideos,
  ]);

  const handleKinkSelect = useCallback(
    (direction) => {
      if (isTransitioning || isAnimating || isResetting) return;

      const newIndex = getRealIndex(
        currentIndex + (direction === 'next' ? 1 : -1),
      );

      setIsAnimating(true);
      setAnimatingDirection(direction);

      setTranslateX(direction === 'next' ? -100 : 100);

      setTimeout(() => {
        flushSync(() => {
          setIsResetting(true);
          setIsAnimating(false);
          setAnimatingDirection(null);
          setTranslateX(0);
          setCurrentIndex(newIndex);
        });

        if (onSelect) {
          onSelect(items[newIndex].value);
        }

        // Сразу начинаем воспроизведение нового активного видео
        requestAnimationFrame(() => {
          setIsResetting(false);

          // Воспроизводим активное видео (offset 0)
          const activeVideo = videoRefs.current[0];
          if (activeVideo && activeVideo.paused) {
            activeVideo.currentTime = 0;
            activeVideo.play().catch(() => {});
          }
        });
      }, 400);
    },
    [isTransitioning, isAnimating, isResetting, currentIndex, items, onSelect],
  );

  const handleCardClick = useCallback(
    (direction) => {
      if (isTransitioning || isAnimating) return;
      handleKinkSelect(direction);
    },
    [isTransitioning, isAnimating, handleKinkSelect],
  );

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setStartX(touch.clientX);
    setDragOffset(0);
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const diffX = touch.clientX - startX;

    if (Math.abs(diffX) > 10) {
      e.preventDefault();
      setIsDragging(true);
      setDragOffset(diffX);
    }
  };

  const handleTouchEnd = (e) => {
    const wasDragging = isDragging;

    if (isDragging) {
      e.preventDefault();
    }

    setIsDragging(false);

    if (Math.abs(dragOffset) > 50) {
      if (dragOffset > 0) {
        handleKinkSelect('prev');
      } else {
        handleKinkSelect('next');
      }
    } else if (!wasDragging) {
      setDragOffset(0);
      return;
    }

    setDragOffset(0);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setStartX(e.clientX);
    setDragOffset(0);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const diff = e.clientX - startX;
    setDragOffset(diff);
  };

  const handleMouseUp = (e) => {
    if (isDragging) {
      e.preventDefault();
    }

    setIsDragging(false);

    if (Math.abs(dragOffset) > 50) {
      if (dragOffset > 0) {
        handleKinkSelect('prev');
      } else {
        handleKinkSelect('next');
      }
    }

    setDragOffset(0);
  };

  const handleNavClick = (direction) => {
    handleKinkSelect(direction);
  };

  const handleVideoError = useCallback((itemValue) => {
    setVideoErrors((prev) => ({ ...prev, [itemValue]: true }));
  }, []);

  const handleVideoMouseEnter = useCallback(
    (e, isActive) => {
      if (
        shouldPlayOnHoverOrSelected &&
        !isMobile &&
        e.target.paused &&
        isActive
      ) {
        e.target.currentTime = 0;
        e.target.play().catch(() => {});
      }
    },
    [shouldPlayOnHoverOrSelected, isMobile],
  );

  const handleVideoMouseLeave = useCallback(
    (e, isActive) => {
      if (
        shouldPlayOnHoverOrSelected &&
        !isMobile &&
        !e.target.paused &&
        !isActive
      ) {
        e.target.pause();
        e.target.currentTime = 0;
      }
    },
    [shouldPlayOnHoverOrSelected, isMobile],
  );

  const handleVideoClick = useCallback(
    (e) => {
      if (animationMode === 'MOUSEOVER' && isMobile) {
        if (e.target.paused) {
          e.target.currentTime = 0;
          e.target.play().catch(() => {});
        } else {
          e.target.pause();
          e.target.currentTime = 0;
        }
      }
    },
    [animationMode, isMobile],
  );

  const renderMedia = (item, offset, isActive) => {
    const videoSource = item.video || item.videoSrc;
    const shouldShowVideo =
      (forceShowVideos || shouldShowVideos) &&
      videoSource &&
      !videoErrors[item.value];

    if (!shouldShowVideo) {
      return <LazyImage src={item.image} alt={item.title || item.alt} />;
    }

    return (
      <video
        ref={(el) => (videoRefs.current[offset] = el)}
        key={`video-${offset}`}
        {...getVideoProps()}
        preload="auto"
        className="kinks-video option-image"
        onMouseEnter={(e) => handleVideoMouseEnter(e, isActive)}
        onMouseLeave={(e) => handleVideoMouseLeave(e, isActive)}
        onClick={handleVideoClick}
        onError={() => handleVideoError(item.value || item.id)}
      />
    );
  };

  return (
    <div className="kinks-block">
      <QuizTitle>{title}</QuizTitle>

      <div
        className="kinks-carousel-container"
        style={{
          // transform: carouselScale !== 1 ? `scale(${carouselScale})` : undefined,
          transformOrigin: 'center center',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          if (isDragging) handleMouseUp();
        }}
      >
        <div
          className="kinks-carousel-track"
          style={{
            transform: `translateX(calc(-100% + ${translateX}% + ${dragOffset}px))`,
            transition:
              isDragging || isResetting || (translateX === 0 && !isAnimating)
                ? 'none'
                : 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {[-1, 0, 1].map((offset) => {
            const index = getRealIndex(currentIndex + offset);
            const item = items[index];

            let isActive = false;
            if (!animatingDirection && translateX === 0 && offset === 0) {
              isActive = true;
            }
            const className = isActive ? 'active' : 'side';

            return (
              <div
                key={offset}
                className={`kinks-card-wrapper ${className}`}
                style={{
                  left: `${(offset + 1) * 100}%`,
                  width: '100%',
                  position: 'absolute',
                  pointerEvents: isAnimating ? 'none' : 'auto',
                }}
                onClick={() =>
                  !isActive &&
                  !isAnimating &&
                  handleCardClick(offset === -1 ? 'prev' : 'next')
                }
              >
                <div
                  className={`kinks-card step0-option ${
                    isActive ? 'selected' : 'not-selected'
                  }`}
                >
                  <div className={imageContainerClass}>
                    {renderMedia(item, offset, isActive)}
                  </div>
                  <div className="step0-image-title">
                    {t(item.title || item.text)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation arrows */}
        <button
          className="carousel-nav carousel-nav-left"
          onClick={() => handleNavClick('prev')}
          disabled={isTransitioning || isAnimating}
        >
          <span>‹</span>
        </button>

        <button
          className="carousel-nav carousel-nav-right"
          onClick={() => handleNavClick('next')}
          disabled={isTransitioning || isAnimating}
        >
          <span>›</span>
        </button>
      </div>

      {/* Индикаторы */}
      <div className="kinks-indicators">
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`kinks-indicator ${
              currentIndex === index ? 'active' : ''
            }`}
            onClick={() => {
              if (isAnimating) return;
              const diff = index - currentIndex;
              if (diff !== 0) {
                handleKinkSelect(diff > 0 ? 'next' : 'prev');
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default VideoCarousel;
